import { stringify } from 'query-string';

import timedFetch from 'middleware/timedFetch';

/**
 * Get detail for a specific product
 * @param  {object}   cloudCatConfig  cloud catalog configuration information
 * @param  {object}   criteria        product detail request criteria
 * @param  {function} [fetcher=fetch] fetch or fetch like implementation
 * @return {object}                   promise
 */
export function productBundle({ url, siteId, subsiteId }, { asin, stockId, styleId, productId, includeTsdImages = false, includeOosSizing = false, includeOos = false }, fetcher = timedFetch('cloudCatalogProduct')) {
  const qs = stringify({
    asin,
    autolink: 'brandProductName',
    entireProduct: true,
    includeBrand: true,
    includeImages: true,
    includeOos,
    includeOosSizing,
    includeSizing: true,
    includeTsdImages,
    includes: 'preferredSubsite,hardLaunchDate,drop,finalSale,taxonomyAttributes',
    productId,
    siteId,
    subsiteId,
    stockId,
    styleId
  });
  const productUrl = `${url}/v3/productBundle?${qs}`;
  return fetcher(productUrl);
}

/**
* Get product review for a product
* @param  {object}   cloudcatalog    cloud catalog info
* @param  {object}   criteria        product reviews request criteria
* @param  {function} [fetcher=fetch] fetch or fetch like implementation
* @return {object}                   promise
*/
export function productReviews({ url }, criteria, fetcher = timedFetch('review')) {
  return fetcher(`${url}/v2/reviews?${stringify(criteria)}`);
}

/**
 * Get a list of all brands
 * @param  {object}   url             cloud catalog api endpoint
 * @param  {object}   siteId          site id
 * @param  {function} [fetcher=fetch] fetch or fetch like implementation
 * @return {object}                   promise
 */
export function brandList({ url, siteId }, fetcher = timedFetch('cloudCatalogBrandList')) {
  return fetcher(`${url}/v1/brandList?siteId=${siteId}`);
}

/**
* Get products related to the one with the given product ID
* @param  {object}   url                 cloud catalog API endpoint
* @param  {object}   {productId, siteId} product ID, site ID
* @param  {function} [fetcher=fetch]     fetch or fetch like implementation
* @return {object}                       promise
*/
export function relatedProducts({ url, siteId }, { productId, hydraSMBE }, fetcher = timedFetch('cloudCatalogRelatedProducts')) {
  return fetcher(`${url}/v1/relatedProducts?productId=${productId}&siteId=${siteId}${hydraSMBE ? '&extendedSizeBreakMerge=true' : ''}`);
}

/**
* Returns product real-time Amazon buyability data for a given style ID
* @param  {object}   url                 cloud catalog API endpoint
* @param  {object}   {styleId, siteId} style ID, site ID
* @param  {function} [fetcher=fetch]     fetch or fetch like implementation
* @return {object}                       promise
*/
export function decorateReco({ url, siteId }, { styleIds }, fetcher = timedFetch('decorateReco')) {
  return fetcher(`${url}/v1/decorateReco?styleIds=${styleIds.join(',')}&siteId=${siteId}`);
}

/**
* Returns count of likes for a product, defaulting to heart list
* https://api.zcloudcat.com/v1/listItemCounts?siteId=1&type=h&
* @param  {object}   url                     cloud catalog API endpoint
* @param  {object}   {itemIds, type, siteId} itemIds (array), type(h for hearts), site ID (1/2)
* @param  {function} [fetcher=fetch]         fetch or fetch like implementation
* @return {object}                           promise
*/
export function likeCounts({ url, siteId }, { styleIds, type = 'h' }, fetcher = timedFetch('likeCounts')) {
  return fetcher(`${url}/v1/listItemCounts?itemIds=${styleIds.join(',')}&type=${type}&siteId=${siteId}`);
}

/**
* Tracks an event for the brand ad dashboard
* https://api.zcloudcat.com/v1/recordEvent?eventType=click&styleId=2834092&sessionId=p29314898071234
* @param  {object}   url                     cloud catalog API endpoint
* @param  {object}   {itemIds, type, siteId} adId, styleId, eventType, sessionId
* @param  {function} [fetcher=fetch]         fetch or fetch like implementation
* @return {void}                               nothing
*/
export function trackAdEvent({ url }, data, fetcher = timedFetch('trackAdEvent')) {
  const headers = {
    'Content-Type': 'application/json'
  };
  fetcher(`${url}/v1/recordEvent`, {
    method: 'post',
    headers,
    body: JSON.stringify(data)
  });
}

/**
* Returns a list of sponsored products given a search term
* https://api.zcloudcat.com/v1/slotContent?slot=searchRow2&key=bluesneakers&limit=4
* @param  {object}  url                      cloud catalog API endpoint
* @param  {object}  params                   list of query params
* @param  {string}  key                      search term
* @param  {string}  slot                     slot to display ads
* @param  {number}  limit                    limit products returned
* @param  {boolean} defaultOrder             Randomize or return items in original list
* @param  {function} [fetcher=fetch]         fetch or fetch like implementation
* @return {object}                           promise
*/
export const fetchSponsoredProducts = ({ url }, { slot = 'searchRow2', limit = 4, defaultOrder = true, key = '' }, fetcher = timedFetch('sponsoredProducts')) => fetcher(`${url}/v1/slotContent?slot=${slot}&key=${key}&limit=${limit}&defaultOrder=${defaultOrder}`);
